import { AnalysisRun, AnalysisRunService } from "./analysis-run.service";
import { officeCall, AnalysisError } from "../utils/analysis-async";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { MailUtils } from "../utils/mail.utils";
import { HttpClient } from "@angular/common/http";
import { ExchangeRequests } from "../models/exchange.requests";
import { LogService } from "./log.service";
import { StringUtils } from "../utils/string.utils";
import { ATTRIBUTES, HOST_NAMES, RESULT_STATUS, TAGS } from "../models/constants";

@Injectable()
export class MessageService {
  private legacyHeaders: HTMLCollection;
  private modernHeaders: any;

  private messageSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public messageChanged: Observable<any> = this.messageSubject.asObservable();

  constructor(
      private runs: AnalysisRunService,
      @Inject(HttpClient) private http: HttpClient,
      @Inject(LogService) private log: LogService,
  ) {}

  public async initialize(run: AnalysisRun = this.runs.current): Promise<void> {
    run.assertActive();
    this.modernHeaders = null;
    this.legacyHeaders = null;
    if (!run.item) throw new AnalysisError('unavailable', 'No message selected');
    if (!this.isMobileHost() && !this.supportsMailVersion("1.5")) {
      const xml = await officeCall<string>(callback => Office.context.mailbox.makeEwsRequestAsync(
        ExchangeRequests.getItemRequest(run.item.itemId), callback), 'Message headers', run);
      const headers = this.getXMLMessageHeaders(MailUtils.parseXmlResponse(xml));
      run.assertActive();
      this.legacyHeaders = headers;
    } else {
      const raw = await officeCall<string>(callback => run.item.getAllInternetHeadersAsync(callback), 'Message headers', run);
      const headers = StringUtils.normalizeMessageHeaders(raw);
      run.assertActive();
      this.modernHeaders = headers;
    }
    run.headersAvailable = true;
    this.messageSubject.next(run.item);
  }

  public isMobileHost(): boolean {
    const hostName: string = Office.context.mailbox.diagnostics.hostName;
    return hostName === HOST_NAMES.OUTLOOK_IOS || hostName === HOST_NAMES.OUTLOOK_ANDROID;
  }

  public isWebHost(): boolean {
    const hostName: string = Office.context.mailbox.diagnostics.hostName;
    return hostName === HOST_NAMES.OUTLOOK_WEB_APP;
  }

  public async markMessageAsJunk(): Promise<void> {
    if (!this.isMobileHost()) {
      await this.markAsJunkExchange();
    } else {
      // todo: add support for mobile
      // call REST API
    }
  }

  private async markAsJunkExchange(): Promise<void> {
    return new Promise<void>((resolve) => {
      Office.context.mailbox.makeEwsRequestAsync(
        ExchangeRequests.markAsJunkRequest(Office.context.mailbox.item!.itemId),
        (result) => {
          if (result.status.toString() === RESULT_STATUS.SUCCEEDED) {
            resolve();
          } else {
            this.log.logException(
              new Error(`Error marking message as Junk - ${StringUtils.formatOfficeError(result.error)}`)
            );
            resolve();
          }
        }
      );
    });
  }

  public async createBlockRule(emailOrDomain: string): Promise<void> {
    if (!this.isMobileHost()) {
      // await this.createBlockRuleExchange(emailOrDomain);
      await this.createBlockRuleREST(emailOrDomain);
    } else {
      // todo: add support for mobile
      // call REST API
    }
  }

  private async createBlockRuleExchange(emailOrDomain: string): Promise<void> {
    return new Promise<void>((resolve) => {
      Office.context.mailbox.makeEwsRequestAsync(
        ExchangeRequests.getInboxRulesRequest(Office.context.mailbox.userProfile.emailAddress),
        (result) => {
          console.log(result.value);
          if (result.status.toString() === RESULT_STATUS.SUCCEEDED) {
            resolve();
          } else {
            resolve();
          }
        }
      );
    });
  }

  private async createBlockRuleREST(emailOrDomain: string): Promise<void> {
    return new Promise<void>((resolve) => {
      Office.context.mailbox.getCallbackTokenAsync({ isRest: true }, (result: Office.AsyncResult<string>) => {
        console.log(result.value);
        if (result.status.toString() === RESULT_STATUS.SUCCEEDED) {
          const token: string = result.value;
          const url: string = `${Office.context.mailbox.restUrl}/v2.0/me/mailFolders/inbox/messageRules`;
          this.http.get(url, { headers: { Authorization: `Bearer ${token}` } }).subscribe((response: any) => {
            console.log(response);
            resolve();
          });
        } else {
          resolve();
        }
      });
    });
  }

  public getMessageHeaderValue(header: string): string {
    if (!this.runs.current?.headersAvailable) throw new AnalysisError("unavailable", "Message headers unavailable");
    let headerValue: string = "";
    if (!this.isMobileHost() && !this.supportsMailVersion("1.5")) {
      if (this.legacyHeaders && this.legacyHeaders.length > 0) {
        for (let i: number = 0; i < this.legacyHeaders.length; i++) {
          const headerElement: Element = this.legacyHeaders.item(i)!;
          const headerName: string = headerElement.getAttribute(ATTRIBUTES.HEADER_NAME)!;

          if (headerName === header) {
            headerValue = headerElement.childNodes[0].nodeValue!;
            console.log(headerValue);
            break;
          }
        }
      } else {
        const error: Error = Error(`No headers available for message ${Office.context.mailbox.item!.itemId}`);
        this.log.logException(error);
        throw error;
      }
    } else {
      if (this.modernHeaders) {
        headerValue = StringUtils.extractHeader(this.modernHeaders, header);
      }
    }

    return headerValue;
  }

  public returnMessageHeaders(){
    if (this.legacyHeaders && this.legacyHeaders.length > 0)
      return this.legacyHeaders;

    return this.modernHeaders;
  }

  private getXMLMessageHeaders(xmlDoc: Document): HTMLCollectionOf<Element> {
    console.log(xmlDoc);

    let headers: HTMLCollectionOf<Element> = xmlDoc.getElementsByTagName(TAGS.INTERNET_MSG_HEADER);

    if (!headers || headers.length === 0) {
      headers = xmlDoc.getElementsByTagName(TAGS.T_INTERNET_MSG_HEADER);
    }

    return headers;
  }

  private supportsMailVersion(version: string): boolean {
    return Office.context.requirements.isSetSupported("Mailbox", version);
  }


  /**
   * When replaced getting message header to use Office.context.mailbox.item.getAllInternetHeadersAsync it returns string
   * and adds these characters (=?us-ascii?Q?) when printing the message header in the Log file
   * So this function removes those characters
   */
  decodeMimeHeader(header: string): string {
    const regex = /=\?([^?]+)\?([BQ])\?([^?]+)\?=/gi;

    return header.replace(regex, (_, charset, encoding, encodedText) => {
      if (encoding.toUpperCase() === 'Q') {
        // Decode Quoted-Printable
        return encodedText
          .replace(/_/g, '') // Replace underscores with spaces
          .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) =>
            String.fromCharCode(parseInt(hex, 16))
          );
      } else if (encoding.toUpperCase() === 'B') {
        // Decode Base64
        try {
          return atob(encodedText); // Decode Base64
        } catch {
          return encodedText; // Fallback if decoding fails
        }
      }
      return encodedText; // Return as-is if no decoding is applied
    });
  }

  public getHeadersAsString(): String {
    if(this.modernHeaders)
      return this.decodeMimeHeader(this.modernHeaders)

    else if(this.legacyHeaders && this.legacyHeaders.length > 0) {
      let result = "";
      for (let i: number = 0; i < this.legacyHeaders.length; i++) {
        const headerElement: Element = this.legacyHeaders.item(i)!;
        result +=  headerElement.outerHTML + '\n';
      }
      return result;
    }
    return "";
  }
}
